import * as React from 'react'
import { Box, Button, Card } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { useDispatch, useSelector } from 'react-redux'
import Widget from '../Widget'
import {
  fetchScarletWellData as fetchScarletWell,
  fetchScarletWellFirebaseData as fetchScarletWellFirebase,
  selectScarletWellData,
  selectScarletWellStatus,
} from './scarletWellSlice'
import scarletWellWidgetStyles from './scarletWellWidgetStyles'
import Loading from '../helpers/Loading'
import { ErrorMessage } from '../../DashboardComponents/ErrorMessages/ErrorMsg'
import SearchBar from '../../DashboardComponents/SearchBar/SearchBar'
import {
  RutgersTab,
  RutgersTabs,
} from '../../DashboardComponents/Tabs/RutgersTabs'
import {
  TabPanel,
  a11yProps,
} from '../../DashboardComponents/TabPanel/TabPanel'
import SWeventList from './ScarletWellEventList'
import IconPicker from '../../../apps/AppComponents/IconPicker/IconPicker'
import { logDashboardEvent } from '../../events'
import WidgetFooterButton from '../CommuterResources/WidgetFooterButton'
import { selectMyProfileData } from '../MyProfile/myProfileSlice'
import { selectDarkMode } from '../../../settings/settingsSlice'

function ScarletWell({
  className,
  title,
  showNote,
  noteMessage,
  notePosition,
  noteBackground,
  noteIcon,
  noteUrl,
  noteFontColor,
  widgetId,
}) {
  const dispatch = useDispatch()
  const widgetData = useSelector(selectScarletWellData)
  const widgetCallState = useSelector(selectScarletWellStatus)
  const isDark = useSelector(selectDarkMode)

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(fetchScarletWellFirebase()),
          dispatch(fetchScarletWell()),
        ])
      } catch (err) {
        console.log('Error fetching Scarlet Well Data', err)
      }
    }
    fetchData()
  }, [])

  return (
    <Widget className={className}>
      <Widget.Header title={title} category="default" />
      <Widget.Content
        showNote={showNote}
        noteMessage={noteMessage}
        notePosition={notePosition}
        noteBackground={noteBackground}
        noteIcon={noteIcon}
        noteUrl={noteUrl}
        noteFontColor={noteFontColor}
      >
        {renderScarletWellState(
          widgetCallState,
          widgetData,
          isDark,
          title,
          widgetId,
        )}
      </Widget.Content>
    </Widget>
  )
}

function renderScarletWellState(
  widgetCallState,
  widgetData,
  isDark,
  title,
  widgetId,
) {
  switch (widgetCallState) {
    case 'fulfilled':
      return (
        <Loaded
          widgetData={widgetData}
          isDark={isDark}
          title={title}
          widgetId={widgetId}
        />
      )
    case 'pending':
      return <Loading />
    case 'error':
      return (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <ErrorMessage />
        </Grid>
      )
    default:
      return (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item></Grid>
        </Grid>
      )
  }
}

function Loaded({ widgetData, isDark, title, widgetId }) {
  const profileData = useSelector(selectMyProfileData)
  const name = profileData.firstName
  const classes = scarletWellWidgetStyles()
  const events = widgetData?.events ? widgetData.events : []
  const [searchQuery, setSearchQuery] = React.useState('')
  const dispatch = useDispatch()
  // call search url with query string in query link
  const handleSearchQuery = (searchTerm) => {
    const redirectString = searchTerm.toString().split(' ').join('%20')
    window.open(
      `${widgetData.eventsTab.searchBaseURL}${redirectString}`,
      '_blank',
    )
  }
  const [currentTable, setCurrentTable] = React.useState(0)
  const handleChange = (event, newValue) => {
    setCurrentTable(newValue)
  }

  const handleClick = (url, objectId) => {
    const objectID = objectId ?? title
    if (widgetId && dispatch) {
      logDashboardEvent(dispatch, 'dashboardLinkEvent', widgetId, objectID, url)
      window.open(url)
    }
  }
  return (
    <Box
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      className={classes.gridContainer}
    >
      <Box item className={classes.modalTabs}>
        <RutgersTabs
          value={currentTable}
          onChange={handleChange}
          aria-label="Top Tab"
        >
          <RutgersTab label="Events" {...a11yProps(0)} />
          <RutgersTab label="Check-in" {...a11yProps(1)} />
        </RutgersTabs>
        <TabPanel value={currentTable} index={0}>
          <Box
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Box item className={classes.searchBarContainer}>
              <Button
                aria-label="Calendar"
                onClick={() =>
                  handleClick(
                    widgetData.eventsTab.calendarLink,
                    'Calendar Button',
                  )
                }
                className={classes.searchBarButton}
              >
                <IconPicker
                  iconName="far fa-calendar"
                  color={isDark ? '#eeeeee' : '#cc0033'}
                  size="2x"
                />
              </Button>

              <Box className={classes.searchBar}>
                <SearchBar
                  placeholder="Search Events"
                  value={searchQuery}
                  webViewMode
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onRequestSearch={() => handleSearchQuery(searchQuery)}
                  onCancelSearch={() => setSearchQuery('')}
                />
              </Box>
              <Button
                aria-label="Filter"
                onClick={() =>
                  handleClick(widgetData.eventsTab.filterLink, 'Filter Button')
                }
                className={classes.searchBarButton}
              >
                <IconPicker
                  iconName="far fa-filter"
                  color={isDark ? '#eeeeee' : '#cc0033'}
                  size="2x"
                />
              </Button>
            </Box>
            <SWeventList
              events={events}
              handleClick={handleClick}
              backupData={null}
            />
          </Box>
        </TabPanel>
        <TabPanel value={currentTable} index={1} className={classes.tabPanel1}>
          <Box className={classes.checkinRoot}>
            <Box>
              <img
                className={classes.mainImage}
                src={widgetData.checkInTab.checkInImage}
                alt="check in"
              />
              <Card
                variant="outlined"
                className={classes.checkInCard}
                style={{ color: isDark ? '#eeeeee' : '#cc0033' }}
              >
                <Typography
                  variant="h2"
                  component="div"
                  className={classes.checkinText}
                >
                  Hello {name}!
                </Typography>
                <Typography variant="h5" className={classes.checkinText}>
                  {widgetData.checkInTab.checkInText}
                </Typography>
                <WidgetFooterButton
                  title={widgetData.checkInTab.checkInButtonText}
                  handleClick={() =>
                    handleClick(
                      widgetData.checkInTab.mainButton,
                      'Check in Button',
                    )
                  }
                />
              </Card>
            </Box>
          </Box>
        </TabPanel>
      </Box>
    </Box>
  )
}

export default ScarletWell
